import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { getAllCartItems } from "./addToCart";

const initialState = {
    isLoading: false,
    isLoadingLogin: false,
    error: null,
    registerUser: {},
    loginUser: {},
    userAccessToken: null,
    userForgotPassword: {},
};

const slice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
            state.error = null;
        },
        startLoadingLogin(state) {
            state.isLoadingLogin = true;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.isLoadingLogin = false;
            state.error = action.payload;
        },
        getRegisterSuccess(state, action) {
            state.isLoading = false;
            state.registerUser = action.payload;
        },
        getLoginSuccess(state, action) {
            state.isLoading = false;
            state.isLoadingLogin = false;
            state.loginUser = action.payload;
        },
        getLoginAccessTokenSuccess(state, action) {
            state.isLoadingLogin = false;
            state.userAccessToken = action.payload;
        },
        getForgotPasswordSuccess(state, action) {
            state.isLoading = false;
            state.userForgotPassword = action.payload;
        },
    },
});

export const {
    startLoading,
    startLoadingLogin,
    hasError,
    getRegisterSuccess,
    getLoginSuccess,
    getLoginAccessTokenSuccess,
    getForgotPasswordSuccess,
} = slice.actions;

export default slice.reducer;

export function postRegisterUser(formData, reset, toast, navigate) {
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      const response = await axios.post("/register", formData);
      dispatch(getRegisterSuccess(response?.data));

      if (response?.data?.status === true) {
        reset();
        return { success: true };
      } else {
        toast.error("Ensure your information is correct and try registering again");
        return { success: false };
      }
    } catch (error) {
      reset();
      console.error("Error during registration:", error);

      const { message, errors } = error?.response?.data || {};

      if (message === "Validation error" && errors) {
        if (errors.email) {
          toast.error(errors.email[0]);
        }
        if (errors.contact) {
          toast.error(errors.contact[0]);
        }
        if (errors.password) {
          toast.error(errors.password[0]);
        }
        if (errors.name) {
          toast.error(errors.name[0]);
        }
      } else {
        toast.error(message || "Something went wrong during registration.");
      }

      dispatch(hasError(message));
      return { success: false };
    }
  };
}

export function postLoginUser(payload, toast, reset, navigate) {
    return async (dispatch) => {
        try {
            dispatch(startLoadingLogin());

            const response = await axios.post("/login", payload);

            if(response?.data?.status === true) {
                dispatch(getLoginSuccess(response.data.user));
                dispatch(getLoginAccessTokenSuccess(response.data.access_token));
                localStorage.removeItem("cart_id");
                localStorage.setItem("user", JSON.stringify(response.data.user));
                localStorage.setItem("cart_id", response.data.cart_id);
                localStorage.setItem("accessToken", response.data.access_token);

                reset();
                toast.success("You're Logged In! Explore New Updates or Start Using Your Account.");

                const cart_id = response.data.cart_id;
                const customer_id = response.data.user.id;
                const token = response.data.access_token;

                dispatch(getAllCartItems(customer_id, { status: true }));
            } else {
                toast.error("Check your credentials and try again");
            }
        } catch (error) {
            reset();

            const message = error?.response?.data?.message || "Login failed";
            const data = error?.response?.data;

            if (message === "phone_unverified") {
                toast.info("Phone verification required");

                navigate("/verify-phone", {
                    state: {
                        user_id: data.unverified_user_id,
                        phone: data.phone,
                    },
                });

                return;
            }

            if (message === "unverified") {
                toast.info("Email verification required");

                navigate("/verify-email-pending", {
                    state: {
                        email: payload.email,
                    },
                });

                return;
            }

            toast.error(message);
            dispatch(hasError(message));
        }
    };
}

export function resendVerificationEmail(email, toast) {
    return async () => {
        try {
            await axios.post("/email/resend-verification", { email });
            toast.success("Verification email resent! Please check your inbox.");
        } catch (error) {
            toast.error(
                error?.response?.data?.message || "Failed to resend verification email."
            );
        }
    };
}

export async function resendVerificationPhone(userId, phone, setLoading, setResent, toast) {
  if (!userId || !phone) {
    toast.error('Missing user ID or phone number.');
    return;
  }

  setLoading(true);
  try {
    await axios.post('/resend-phone-code', { user_id: userId });
    toast.success('Verification code resent!');
    setResent(true);
  } catch (error) {
    toast.error(error?.response?.data?.message || 'Failed to resend code');
  } finally {
    setLoading(false);
  }
}

export async function verifyPhoneCode(userId, code, setVerifying, toast) {
  if (!userId || !code) {
    toast.error("Invalid user or code.");
    return { success: false };
  }

  setVerifying(true);
  try {
    const response = await axios.post("/verify-phone-code", {
      user_id: userId,
      code,
    });

    if (response.data.status === true) {
      toast.success("Phone verified successfully!");
      setVerifying(false);
      return { success: true };
    } else {
      toast.error(response.data.message || "Invalid verification code");
      setVerifying(false);
      return { success: false };
    }
  } catch (error) {
    toast.error(
      error.response?.data?.message || "Verification failed. Please try again."
    );
    setVerifying(false);
    return { success: false };
  }
}

export function postForgotPasswordUser(payload, toast, reset) {
    return async (dispatch) => {
        try {
            dispatch(startLoading());
            const response = await axios.post("/forgot-password", payload);
            dispatch(getForgotPasswordSuccess(response?.data));

            if (response?.data?.status === true) {
                toast.success("Check your email to proceed with resetting your password.");
                reset();
            } else {
                toast.error("Double-check your email address and try again");
            }
        } catch (error) {
            reset();
            console.error("Error during forgot password request:", error);
            dispatch(hasError(error.response?.data?.message || "Forgot password request failed"));
            toast.error(error.response?.data?.message || "Double-check your email address and try again");
        }
    };
}

export function postResetPasswordUser(payload, toast, reset) {
    return async (dispatch) => {
        try {
            dispatch(startLoading());
            const response = await axios.post("/reset-password", payload);

            if (response?.data?.status === true) {
                toast.success("Your password has been successfully changed. Please log in");
                reset();
            } else {
                toast.error("Ensure the link is correct and not expired, then try again.");
            }
        } catch (error) {
            reset();
            console.error("Error during reset password request:", error);
            dispatch(hasError(error.response?.data?.message || "Reset password request failed"));
            toast.error(error.response?.data?.message || "Ensure the link is correct and not expired, then try again.");
        }
    };
}


export function postLogoutUser(toast, navigate) {
    return async (dispatch) => {
        dispatch(startLoading());

        try {
            const accessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : '';

            const headers = {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            };

            const response = await axios.post('/logout', {}, { headers });

            if (response?.data?.status) { // Simplified check
                // Clear local storage
                localStorage.removeItem('user');
                localStorage.removeItem('accessToken');
                localStorage.removeItem('shipAddress');
                localStorage.removeItem('billAddress');
                localStorage.removeItem('cart_id');
                
                // Dispatch success actions
                dispatch(getLoginSuccess(null));
                dispatch(getLoginAccessTokenSuccess(null));

                // Navigate to login page
                navigate('/login');
                window.location.reload();
                // Show success toast message
                toast.success("You’ve successfully logged out");
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
                return Promise.resolve();
            } else {
                // If the response status is not as expected
                throw new Error(response?.data?.message || "Logout failed");
            }
        } catch (error) {
            // Improved error handling
            const errorMessage = error.response?.data?.message || "Logout failed";
            console.error('Error Response:', error.response);

            // Dispatch error action
            dispatch(hasError(errorMessage));

            // Optionally show error toast message
            toast.error(errorMessage);
        }
    };
}

